import React from "react";
import { observer } from "mobx-react-lite";
import { Badge, Box, Flex, HStack, Heading, Text, keyframes } from "@chakra-ui/react";
import { ProfileViewModel } from "../ProfileViewModel";

// Płynące animowane tło (przesuwanie pozycji dużego gradientu).
const flow = keyframes`
  0% { background-position: 0% 50%; }
  50% { background-position: 100% 50%; }
  100% { background-position: 0% 50%; }
`;
const shimmer = keyframes`
  0%, 100% { opacity: 0.55; transform: translateX(-12%); }
  50% { opacity: 1; transform: translateX(12%); }
`;

/**
 * Portfel tożsamości — APEX zabezpieczeń ("król").
 * AKTYWNA → całą kartę pokrywa zielone animowane tło (żywe potwierdzenie najwyższego poziomu ochrony).
 * NIEAKTYWNA → spokojny fioletowy gradient + biały spód z instrukcją.
 */
export const IdentityKingCard: React.FC<{ model: ProfileViewModel }> = observer(({ model }) => {
  const active = model.identityKey;

  if (active) {
    return (
      <Box
        position="relative"
        overflow="hidden"
        borderRadius="22px"
        gridColumn={{ md: "1 / -1" }}
        color="white"
        p={{ base: "24px", md: "30px" }}
        boxShadow="0 16px 40px rgba(22,193,114,0.28)"
        sx={{
          background: "linear-gradient(120deg, #0B6B34 0%, #169C53 28%, #1FC172 50%, #169C53 72%, #0B6B34 100%)",
          backgroundSize: "300% 300%",
          animation: `${flow} 9s ease infinite`
        }}
      >
        {/* delikatny przesuwający się połysk */}
        <Box
          position="absolute"
          inset="0"
          pointerEvents="none"
          bgGradient="linear(100deg, transparent 35%, rgba(255,255,255,0.18) 50%, transparent 65%)"
          sx={{ animation: `${shimmer} 6s ease-in-out infinite` }}
        />
        <Box position="relative">
          <Flex align="center" justify="space-between" mb="10px" gap="12px" wrap="wrap">
            <HStack spacing="12px">
              <Box fontSize="26px" filter="drop-shadow(0 2px 6px rgba(0,0,0,0.25))">👑</Box>
              <Heading fontSize={{ base: "18px", md: "20px" }} fontWeight="800">
                Tożsamość cyfrowa (portfel)
              </Heading>
            </HStack>
            <Badge bg="rgba(255,255,255,0.22)" color="white" borderRadius="999px" px="12px" py="4px" fontSize="12px" letterSpacing="0.06em" backdropFilter="blur(4px)">
              ● AKTYWNA
            </Badge>
          </Flex>
          <Text fontSize={{ base: "14px", md: "14.5px" }} color="rgba(255,255,255,0.92)" mb="14px">
            Najsilniejsze zabezpieczenie konta. Suwerenny klucz w Twoim telefonie, podpisuje logowanie
            kryptograficznie. Odzysk <b>wyłącznie z 12 słów</b> — nikt, nawet my, nie wejdzie bez nich.
          </Text>
          <Flex align="center" gap="9px" bg="rgba(255,255,255,0.14)" borderRadius="12px" p="12px 14px" backdropFilter="blur(4px)">
            <Box fontSize="15px">🔐</Box>
            <Text fontSize="13.5px" color="white" fontWeight="500">
              Tożsamość aktywna. Frazę odzysku i ustawienia portfela znajdziesz w aplikacji lubbezpośrednio.
            </Text>
          </Flex>
        </Box>
      </Box>
    );
  }

  return (
    <Box borderRadius="22px" overflow="hidden" boxShadow="0 10px 30px rgba(67,0,152,0.12)" gridColumn={{ md: "1 / -1" }}>
      <Box bgGradient="linear(135deg, #2E0069, #430098 60%, #6A1FC2)" color="white" p={{ base: "22px", md: "26px" }}>
        <Flex align="center" justify="space-between" mb="6px">
          <HStack spacing="10px">
            <Box fontSize="22px">👑</Box>
            <Heading fontSize="18px" fontWeight="800">Tożsamość cyfrowa (portfel)</Heading>
          </HStack>
          <Badge bg="rgba(255,255,255,0.18)" color="white" borderRadius="6px" px="8px">
            Nieaktywna
          </Badge>
        </Flex>
        <Text fontSize="14px" color="rgba(255,255,255,0.88)">
          Najsilniejsze zabezpieczenie konta. Suwerenny klucz w Twoim telefonie, podpisuje logowanie kryptograficznie.
          Odzysk <b>wyłącznie z 12 słów</b> — nikt, nawet my, nie wejdzie bez nich.
        </Text>
      </Box>
      <Box bg="white" p={{ base: "16px 22px", md: "16px 26px" }} border="1px solid rgba(67,0,152,0.08)" borderTop="none" borderBottomRadius="22px">
        <Text fontSize="13.5px" color="gray.500">
          Utwórz tożsamość w aplikacji lubbezpośrednio (iOS / Android) — przeglądarka nie ma bezpiecznej enklawy na klucz.
        </Text>
      </Box>
    </Box>
  );
});
